import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '@/components/Tooltip';
import { CalendarIcon } from '@/components/icons/CalendarIcon';
import { InfoCircleIcon } from '@/components/icons/InfoCircleIcon';
import { PeriodLockedModal } from '@/components/PeriodLockedModal';
import { openPlansWithTracking } from '@/utils/openPlansWithTracking';
import type { WeekStart } from '@/i18n/weekStart';
import { AnalysisPeriodCalendarPopover } from './AnalysisPeriodCalendarPopover';
import {
    renderEnBarDateLabel,
    renderJaBarDateLabel,
} from './analysisDateLabelRich';
import type { PaginationBucket, PeriodRange } from './periodUtils';

export type { PaginationBucket } from './periodUtils';

interface Props {
    bucket: PaginationBucket;
    onBucketChange: (bucket: PaginationBucket) => void;
    periodStart: Date;
    periodEnd: Date;
    weekStart: WeekStart;
    isPaid: boolean;
    onPeriodSelect: (range: PeriodRange) => void;
    onPrev: () => void;
    onNext: () => void;
    isNextDisabled?: boolean;
    /** 分析ページ下部ドック: 期間・前後移動のみの一行表示 */
    variant?: 'default' | 'dockCompact';
    /** dockCompact 時: 行のタップ領域をやや高く（モバイルシート展開時など） */
    dockComfortableHeight?: boolean;
    /** true のとき日付ナビ行にチュートリアル用 id を付与しない（ドック側に id を任せる） */
    omitTutorialDateNavId?: boolean;
    /** true のとき週／月／カスタムタブのみ表示（ドックの日付行と重複させない） */
    hideDateNavigation?: boolean;
    /** ボトムシート等: 週／月／カスタムタブ行を低めの高さに */
    compactBucketTabs?: boolean;
}

export function AnalysisDayPaginationBar({
    bucket,
    onBucketChange,
    periodStart,
    periodEnd,
    weekStart,
    isPaid,
    onPeriodSelect,
    onPrev,
    onNext,
    isNextDisabled = false,
    variant = 'default',
    omitTutorialDateNavId = false,
    hideDateNavigation = false,
    compactBucketTabs = false,
    dockComfortableHeight = false,
}: Props): ReactElement {
    const { i18n, t } = useTranslation('analysis');
    const [pickerOpen, setPickerOpen] = useState(false);
    const [basicPlanModalOpen, setBasicPlanModalOpen] = useState(false);
    const periodTriggerRef = useRef<HTMLDivElement>(null);
    const periodLockedPlansSourceRef = useRef<
        'analysis_custom_shortcut_locked' | 'analysis_custom_range_locked'
    >('analysis_custom_shortcut_locked');

    useEffect(() => {
        if (!pickerOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setPickerOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [pickerOpen]);

    useEffect(() => {
        setPickerOpen(false);
    }, [bucket]);

    const dateLabel = useMemo(() => {
        const locale = i18n.language?.startsWith('ja') ? 'ja-JP' : 'en-US';

        /** 日本語: 数字と「年」「月」「日」の間は詰める。ブロックの区切りのみ「年の後」「月の後」に半角スペース */
        const formatDateStr = (str: string) => {
            if (!i18n.language?.startsWith('ja')) return str;
            return str
                .replace(/\u3000/g, ' ')
                .replace(/(\d)\s+(年|月|日)/g, '$1$2')
                .replace(/年(?=\d)/g, '年 ')
                .replace(/月(?=\d)/g, '月 ')
                .replace(/\s{2,}/g, ' ')
                .trim();
        };

        if (bucket === 'month') {
            return formatDateStr(
                new Intl.DateTimeFormat(locale, {
                    year: 'numeric',
                    month: 'long',
                }).format(periodStart),
            );
        }

        const isJa = i18n.language?.startsWith('ja');
        const enDash = '\u2013';

        if (!isJa) {
            const en = 'en-US';
            const monthShort = (d: Date) =>
                new Intl.DateTimeFormat(en, { month: 'short' }).format(d);
            const fmtEnFull = (d: Date) =>
                new Intl.DateTimeFormat(en, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                }).format(d);

            const y1 = periodStart.getFullYear();
            const y2 = periodEnd.getFullYear();
            const m1 = periodStart.getMonth();
            const m2 = periodEnd.getMonth();
            const d1 = periodStart.getDate();
            const d2 = periodEnd.getDate();

            const sameDay = y1 === y2 && m1 === m2 && d1 === d2;
            if (sameDay) {
                return fmtEnFull(periodStart);
            }

            const sameMonth = y1 === y2 && m1 === m2;
            if (sameMonth) {
                return `${monthShort(periodStart)} ${d1}${enDash}${d2}, ${y1}`;
            }
            if (y1 === y2) {
                return `${monthShort(periodStart)} ${d1} ${enDash} ${monthShort(periodEnd)} ${d2}, ${y1}`;
            }
            return `${fmtEnFull(periodStart)} ${enDash} ${fmtEnFull(periodEnd)}`;
        }

        const fmtFull = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
        const fmtMonthDay = new Intl.DateTimeFormat(locale, {
            month: 'short',
            day: 'numeric',
        });
        const fmtDayOnly = new Intl.DateTimeFormat(locale, {
            day: 'numeric',
        });

        const sameYear = periodStart.getFullYear() === periodEnd.getFullYear();
        const sameMonth =
            sameYear && periodStart.getMonth() === periodEnd.getMonth();

        const startStr = formatDateStr(fmtFull.format(periodStart));
        let endStr: string;
        if (sameMonth) {
            endStr = formatDateStr(fmtDayOnly.format(periodEnd));
        } else if (sameYear) {
            endStr = formatDateStr(fmtMonthDay.format(periodEnd));
        } else {
            endStr = formatDateStr(fmtFull.format(periodEnd));
        }
        return `${startStr} ~ ${endStr}`;
    }, [i18n.language, bucket, periodStart, periodEnd]);

    const dateLabelRich = useMemo(() => {
        const isJa = i18n.language?.startsWith('ja');
        return isJa ? renderJaBarDateLabel(dateLabel) : renderEnBarDateLabel(dateLabel);
    }, [dateLabel, i18n.language]);

    const prevAriaLabel =
        bucket === 'month' ? t('dayPagination.previousMonth') : t('dayPagination.previousWeek');
    const nextAriaLabel =
        bucket === 'month' ? t('dayPagination.nextMonth') : t('dayPagination.nextWeek');

    const prevTooltip =
        bucket === 'month' ? t('dayPagination.tooltip.prevMonth') : t('dayPagination.tooltip.prevWeek');
    const nextTooltip =
        bucket === 'month' ? t('dayPagination.tooltip.nextMonth') : t('dayPagination.tooltip.nextWeek');
    const dateTooltip =
        bucket === 'month'
            ? t('dayPagination.tooltip.pickMonth')
            : bucket === 'custom'
              ? t('dayPagination.tooltip.pickCustom')
              : t('dayPagination.tooltip.pickWeek');

    const showPaging = bucket !== 'custom';

    const tooltipPanelClass =
        'z-[60] max-w-[min(18rem,calc(100vw-2rem))] select-none rounded-md border border-black/10 bg-gray-800 px-2 py-1.5 text-left text-xs leading-snug text-white shadow-lg';

    const bucketHelpIconButtonClass =
        'inline-flex shrink-0 rounded-full p-1 text-gray-400 transition-colors duration-150 hover:bg-gray-800 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-kotomilThemaColor';

    const tabBtn = (b: PaginationBucket, label: string) => (
        <button
            type="button"
            onClick={() => onBucketChange(b)}
            className={`rounded-md transition-all sm:flex-none ${
                compactBucketTabs
                    ? 'flex-none min-w-[2.5rem] px-2.5 py-0.5 text-xs font-semibold sm:min-w-0'
                    : 'flex-1 px-3 py-2 text-sm sm:flex-none sm:px-5 sm:py-2.5 sm:text-base'
            } ${
                bucket === b
                    ? 'bg-white text-gray-800 font-bold'
                    : 'bg-gray-100 text-gray-600 hover:bg-white hover:text-gray-800'
            }`}
        >
            {label}
        </button>
    );

    const isDockCompact = variant === 'dockCompact';
    const dockH = dockComfortableHeight && isDockCompact ? 'h-12' : 'h-11';

    const dateNavigationRow = (
        <div
            id={
                isDockCompact
                    ? 'tutorial-analysis-date-navigation'
                    : omitTutorialDateNavId
                      ? undefined
                      : 'tutorial-analysis-date-navigation'
            }
            className={
                isDockCompact
                    ? 'flex w-full max-w-full items-stretch justify-center gap-2 py-0'
                    : 'flex w-full max-w-full items-stretch justify-center gap-2 py-3 sm:gap-4 sm:py-0'
            }
        >
            {showPaging && (
                <Tooltip content={prevTooltip} side="top" className={tooltipPanelClass}>
                    <button
                        type="button"
                        className={
                            isDockCompact
                                ? `flex ${dockH} w-10 shrink-0 items-center justify-center self-stretch rounded-lg border-2 border-gray-200 bg-white text-base text-gray-600 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900`
                                : 'flex w-9 shrink-0 items-center justify-center self-stretch rounded-lg border-2 border-gray-200 bg-white text-gray-600 shadow-sm transition-all hover:scale-105 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 sm:w-10'
                        }
                        aria-label={prevAriaLabel}
                        onClick={onPrev}
                    >
                        <span
                            className={
                                isDockCompact
                                    ? 'inline-flex size-5 shrink-0 items-center justify-center text-lg leading-none'
                                    : 'inline-flex size-4 shrink-0 items-center justify-center text-base leading-none sm:size-5 sm:text-lg'
                            }
                            aria-hidden={true}
                        >
                            ←
                        </span>
                    </button>
                </Tooltip>
            )}
            <div
                ref={periodTriggerRef}
                className={
                    isDockCompact
                        ? 'relative flex min-w-0 flex-1 flex-col justify-center self-stretch'
                        : 'relative flex min-w-0 flex-1 flex-col items-center justify-center sm:min-w-[22rem] sm:flex-none'
                }
            >
                <Tooltip content={dateTooltip} side="top" className={tooltipPanelClass}>
                    <button
                        type="button"
                        onClick={() => setPickerOpen((o) => !o)}
                        className={
                            isDockCompact
                                ? `group flex ${dockH} w-full min-h-0 items-center justify-center rounded-xl border border-gray-300 bg-white px-2 py-0 text-center font-semibold text-gray-900 tabular-nums shadow-sm transition-colors hover:bg-gray-50`
                                : 'group w-full rounded-2xl border border-gray-300 bg-transparent px-3 py-3 text-center text-2xl text-gray-900 tabular-nums transition-colors hover:bg-gray-100 sm:w-auto sm:px-5 sm:py-2.5 lg:text-3xl'
                        }
                        aria-expanded={pickerOpen}
                        aria-haspopup="dialog"
                        aria-label={t('dayPagination.pickPeriod')}
                    >
                        <span
                            className={
                                isDockCompact
                                    ? 'inline-flex w-full max-w-full items-center justify-center gap-2'
                                    : 'inline-flex w-full max-w-full items-center justify-center gap-2 sm:gap-3.5'
                            }
                        >
                            <CalendarIcon
                                className={
                                    isDockCompact
                                        ? 'size-5 shrink-0 text-gray-500 transition-colors group-hover:text-gray-700'
                                        : 'size-7 shrink-0 text-gray-500 transition-colors group-hover:text-gray-700 sm:size-7 lg:size-8'
                                }
                            />
                            <span
                                className={
                                    isDockCompact
                                        ? 'min-w-0 flex-1 whitespace-normal text-center text-lg font-medium leading-snug tracking-wide'
                                        : 'min-w-0 flex-1 whitespace-normal text-center align-baseline tracking-wide sm:flex-none sm:tracking-wider lg:tracking-[0.14em]'
                                }
                            >
                                {dateLabelRich}
                            </span>
                        </span>
                    </button>
                </Tooltip>
                <AnalysisPeriodCalendarPopover
                    isOpen={pickerOpen}
                    onClose={() => setPickerOpen(false)}
                    anchorRef={periodTriggerRef}
                    bucket={bucket}
                    periodStart={periodStart}
                    periodEnd={periodEnd}
                    weekStart={weekStart}
                    locale={i18n.language?.startsWith('ja') ? 'ja-JP' : 'en-US'}
                    isPaid={isPaid}
                    onSelect={onPeriodSelect}
                    onPaidOnlyShortcutAttempt={
                        isPaid
                            ? undefined
                            : () => {
                                  periodLockedPlansSourceRef.current =
                                      'analysis_custom_shortcut_locked';
                                  setBasicPlanModalOpen(true);
                              }
                    }
                    onLongCustomPeriodBlocked={
                        isPaid
                            ? undefined
                            : () => {
                                  periodLockedPlansSourceRef.current =
                                      'analysis_custom_range_locked';
                                  setBasicPlanModalOpen(true);
                              }
                    }
                />
            </div>
            {showPaging && (
                <Tooltip content={nextTooltip} side="top" className={tooltipPanelClass}>
                    <span className={isDockCompact ? `flex ${dockH} shrink-0 self-stretch items-stretch` : 'flex shrink-0 self-stretch'}>
                        <button
                            type="button"
                            className={
                                isDockCompact
                                    ? 'flex h-full min-h-0 w-10 items-center justify-center rounded-lg border-2 border-gray-200 bg-white text-base text-gray-600 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-50'
                                    : 'flex h-full w-9 items-center justify-center rounded-lg border-2 border-gray-200 bg-white text-gray-600 shadow-sm transition-all hover:scale-105 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-50 sm:w-10'
                            }
                            aria-label={nextAriaLabel}
                            onClick={onNext}
                            disabled={isNextDisabled}
                        >
                            <span
                                className={
                                    isDockCompact
                                        ? 'inline-flex size-5 shrink-0 items-center justify-center text-lg leading-none'
                                        : 'inline-flex size-4 shrink-0 items-center justify-center text-base leading-none sm:size-5 sm:text-lg'
                                }
                                aria-hidden={true}
                            >
                                →
                            </span>
                        </button>
                    </span>
                </Tooltip>
            )}
        </div>
    );

    return (
        <>
            {isDockCompact ? (
                dateNavigationRow
            ) : (
                <div
                    className={`flex w-full max-w-full flex-col items-stretch sm:items-center ${compactBucketTabs ? 'gap-2' : 'gap-3 sm:gap-4'}`}
                >
                    <div
                        className={`flex w-full max-w-full shrink-0 items-center gap-1.5 sm:w-auto sm:gap-2 ${
                            compactBucketTabs ? 'justify-center' : ''
                        }`}
                    >
                        <div
                            id="tutorial-analysis-bucket-tabs"
                            className={`z-10 flex items-stretch justify-stretch rounded-lg border border-gray-200 bg-gray-100 sm:w-auto sm:flex-none sm:items-center sm:justify-center sm:gap-1.5 ${
                                compactBucketTabs
                                    ? 'w-fit max-w-full min-w-0 gap-1 p-1'
                                    : 'min-w-0 flex-1 gap-1 p-2'
                            }`}
                        >
                            {tabBtn('week', t('bucket.week'))}
                            {tabBtn('month', t('bucket.month'))}
                            {tabBtn('custom', t('bucket.custom'))}
                        </div>
                        <Tooltip
                            content={t('dayPagination.help.bucketTabs')}
                            side="top"
                            className={tooltipPanelClass}
                            openOnPress
                        >
                            <button
                                type="button"
                                className={
                                    compactBucketTabs
                                        ? 'inline-flex shrink-0 rounded-full p-0.5 text-gray-400 transition-colors duration-150 hover:bg-gray-800 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-kotomilThemaColor'
                                        : bucketHelpIconButtonClass
                                }
                                aria-label={t('dayPagination.help.ariaBucketTabs')}
                            >
                                <InfoCircleIcon className={compactBucketTabs ? 'size-4' : 'size-5'} />
                            </button>
                        </Tooltip>
                    </div>
                    {!hideDateNavigation ? dateNavigationRow : null}
                </div>
            )}
        <PeriodLockedModal
            isOpen={basicPlanModalOpen}
            onClose={() => setBasicPlanModalOpen(false)}
            overlayZIndex={10080}
            onGoPlans={() =>
                openPlansWithTracking({
                    source: periodLockedPlansSourceRef.current,
                    panelId: 'anDayPagination',
                })
            }
        />
        </>
    );
}
